// Form controls for the branding editor. Each is controlled (value + onChange)
// and styled by editor.css.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronDown, Search } from "lucide-react";
import { GOOGLE_FONTS, ensureGoogleFont, fontStack } from "@widget";

/** Collapsible group of related settings. */
export function Section({ title, children, defaultOpen = true }: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  return (
    <details className="ed-section" open={defaultOpen}>
      <summary>
        {title}
        <ChevronDown size={16} aria-hidden="true" />
      </summary>
      <div className="ed-section-body">{children}</div>
    </details>
  );
}

/** Label + control (+ optional hint) on one row or stacked. */
export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor?: string; hint?: string; children: ReactNode }) {
  return (
    <div className="ed-field">
      <label className="ed-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && <p className="ed-hint">{hint}</p>}
    </div>
  );
}

/** Range slider with a numeric readout. */
export function Slider({
  id,
  value,
  min,
  max,
  step = 1,
  unit = "px",
  disabled,
  onChange,
}: {
  id: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <div className="ed-slider">
      <input id={id} type="range" min={min} max={max} step={step} value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="ed-slider-value">
        {value}
        {unit}
      </span>
    </div>
  );
}

/** Segmented button group for a small set of choices. */
export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="ed-segmented" role="radiogroup">
      {options.map(([v, label]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? "is-on" : ""} onClick={() => onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

/** On/off switch. Pass ariaLabel instead of label for a switch with no visible text (e.g. in a table). */
export function Toggle({
  id,
  checked,
  label,
  ariaLabel,
  onChange,
}: {
  id: string;
  checked: boolean;
  label?: string;
  ariaLabel?: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="ed-toggle" htmlFor={id}>
      <input id={id} type="checkbox" role="switch" aria-label={ariaLabel} checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="ed-toggle-track" aria-hidden="true" />
      {label}
    </label>
  );
}

const HEX = /^#[0-9a-f]{6}$/i;

/** Colour swatch picker + hex text box (the text box accepts pasted brand hex codes). */
export function ColorField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(value);
  // Keep the text box in sync when the value changes elsewhere (picker, reset)
  useEffect(() => setText(value), [value]);
  return (
    <div className="ed-color">
      <input type="color" aria-label={`${label} picker`} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} />
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="ed-hex"
        value={text}
        maxLength={7}
        spellCheck={false}
        onChange={(e) => {
          const v = e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`;
          setText(v);
          if (HEX.test(v)) onChange(v.toUpperCase());
        }}
        onBlur={() => setText(value)}
      />
    </div>
  );
}

/**
 * Searchable Google Fonts picker. Each option is drawn in its own font; faces
 * are loaded lazily as options scroll into view so opening the list is cheap.
 */
export function FontPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const matches = useMemo(() => GOOGLE_FONTS.filter((f) => f.family.toLowerCase().includes(query.toLowerCase())), [query]);

  // The selected font is always loaded (the trigger button renders in it)
  useEffect(() => ensureGoogleFont(value), [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Load each option's font when it becomes visible in the list
  useEffect(() => {
    if (!open || !listRef.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => en.isIntersecting && ensureGoogleFont((en.target as HTMLElement).dataset.family!));
    });
    listRef.current.querySelectorAll("li[data-family]").forEach((li) => io.observe(li));
    return () => io.disconnect();
  }, [open, matches]);

  return (
    <div className="ed-font" ref={ref}>
      <button type="button" className="ed-font-trigger" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} style={{ fontFamily: fontStack(value) }}>
        {value}
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <div className="ed-font-pop">
          <div className="ed-font-search">
            <Search size={14} aria-hidden="true" />
            <input autoFocus placeholder="Search Google Fonts" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <ul role="listbox" ref={listRef}>
            {matches.map((f) => (
              <li
                key={f.family}
                data-family={f.family}
                role="option"
                aria-selected={f.family === value}
                style={{ fontFamily: fontStack(f.family) }}
                onClick={() => {
                  onChange(f.family);
                  setOpen(false);
                  setQuery("");
                }}
              >
                {f.family}
                <span className="ed-font-cat">{f.category}</span>
              </li>
            ))}
            {matches.length === 0 && <li className="ed-font-none">No matching fonts</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
